import { cn } from '@shared/components/shadcn';
import type { ComponentProps, ReactNode } from 'react';
import { Callout } from '../Callout/Callout';
import { Link } from '../Link/Link';

type InfoBannerProps = ComponentProps<'section'> & {
  calloutText: string;
  calloutVariant?: 'accent' | 'secondary';
  children: ReactNode;
  href: string;
  linkText: string;
  linkAriaLabel?: string;
  dataTrackContainer?: string;
  dataTrackFeature?: string;
  dataTrackType?: string;
  linkDataTrackName?: string;
  onLinkClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
};

const BANNER_STYLES = 'bg-background text-foreground dark:bg-container-base dark:text-foreground border-b border-border';

function InfoBanner({
  calloutText,
  calloutVariant,
  children,
  href,
  linkText,
  linkAriaLabel,
  linkDataTrackName,
  onLinkClick,
  className,
  dataTrackContainer,
  dataTrackFeature,
  dataTrackType,
  ...sectionProps
}: InfoBannerProps) {
  const BASE_CLASSES = 'flex flex-row justify-center items-center gap-3 md:gap-2 px-5 py-3 min-h-9 w-full';

  return (
    <section
      {...sectionProps}
      role="banner"
      aria-live="polite"
      className={cn(BASE_CLASSES, BANNER_STYLES, className)}
      data-testid="info-banner"
      data-track-feature={dataTrackFeature}
      data-track-container={dataTrackContainer}
      data-track-type={dataTrackType}
    >
      <div className="flex flex-row items-center gap-3 w-full justify-center">
        <Callout variant={calloutVariant || 'accent'}>{calloutText}</Callout>
        <div className="md:text-center paragraph-explanatory-strong">
          {children}{' '}
          <Link
            href={href}
            aria-label={linkAriaLabel}
            data-track-name={linkDataTrackName}
            data-testid="info-banner-classic-link"
            size="explanatory"
            onClick={e => {
              if (onLinkClick) {
                e.preventDefault();
                onLinkClick(e);
              }
            }}
          >
            {linkText}
          </Link>
        </div>
      </div>
    </section>
  );
}

export { InfoBanner, type InfoBannerProps };
